import React, { useEffect, useState } from 'react';
import { Car, Info } from 'lucide-react';
import { api } from '@/api';

// Year → Make → Model cascading lookup against the PVO vehicle sqft database.
// Calls onChange({ vehicle, includeRoof }) whenever the selection changes (vehicle may be null).
export default function VehiclePicker({ onChange, defaultYear = 2026, showPanels = true, testIdPrefix = 'vp' }) {
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [year, setYear] = useState(defaultYear);
  const [make, setMake] = useState('');
  const [vehicleId, setVehicleId] = useState('');
  const [vehicle, setVehicle] = useState(null);
  const [includeRoof, setIncludeRoof] = useState(true);

  useEffect(() => {
    api.get('/vehicles/years').then(({ data }) => setYears(data.years || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setMake(''); setModels([]); setVehicleId(''); setVehicle(null);
    api.get('/vehicles/makes', { params: { year } }).then(({ data }) => setMakes(data.makes || [])).catch(() => setMakes([]));
  }, [year]);

  useEffect(() => {
    setVehicleId(''); setVehicle(null);
    if (!make) { setModels([]); return; }
    api.get('/vehicles/models', { params: { make, year } }).then(({ data }) => setModels(data.items || [])).catch(() => setModels([]));
  }, [make, year]);

  useEffect(() => {
    if (!vehicleId) { setVehicle(null); return; }
    api.get(`/vehicles/${vehicleId}`).then(({ data }) => setVehicle(data)).catch(() => setVehicle(null));
  }, [vehicleId]);

  useEffect(() => {
    onChange?.({ vehicle, includeRoof });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicle, includeRoof]);

  const sqft = vehicle ? (includeRoof ? vehicle.sqft_with_roof : vehicle.sqft_no_roof) : null;
  const linearFt = sqft != null ? Math.ceil((sqft / 5) * 10) / 10 : null; // 60" roll = 5 ft wide

  return (
    <div data-testid={`${testIdPrefix}-root`}>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <label className="block">
          <div className="mi-eyebrow mb-2 text-white/70">Year</div>
          <select data-testid={`${testIdPrefix}-year`} value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))} className="mi-input">
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="mi-eyebrow mb-2 text-white/70">Make</div>
          <select data-testid={`${testIdPrefix}-make`} value={make} onChange={(e) => setMake(e.target.value)} className="mi-input">
            <option value="">Select make…</option>
            {makes.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="mi-eyebrow mb-2 text-white/70">Model</div>
          <select data-testid={`${testIdPrefix}-model`} value={vehicleId} onChange={(e) => setVehicleId(e.target.value)} className="mi-input" disabled={!make}>
            <option value="">{make ? 'Select model…' : '—'}</option>
            {models.map((m) => (
              <option key={m.id} value={m.id}>{`${m.model} · ${m.year} · ${m.total_sqft} sqft${m.estimated ? ' (est.)' : ''}`}</option>
            ))}
          </select>
        </label>
      </div>

      {vehicle && (
        <div className="mt-4 border mi-line-strong bg-black/50 p-4" data-testid={`${testIdPrefix}-panel`}>
          <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
            <div>
              <div className="mi-eyebrow text-[#E63946] text-[10px] mb-1 flex items-center gap-1.5"><Car className="w-3 h-3" /> {vehicle.label}</div>
              <div className="flex items-end gap-4 flex-wrap">
                <div className="mi-headline text-3xl leading-none" data-testid={`${testIdPrefix}-sqft`}>{sqft} <span className="text-base text-white/50">sqft</span></div>
                <div className="mi-headline text-3xl leading-none text-[#E63946]" data-testid={`${testIdPrefix}-linear-ft`}>
                  {linearFt} <span className="text-base text-white/50">lin ft · 60" media</span>
                </div>
              </div>
              <div className="text-white/40 text-[10px] mi-eyebrow mt-1">INCLUDES 6" BLEED · {includeRoof ? 'WITH ROOF' : 'ROOF EXCLUDED'} · LIN FT = SQFT ÷ 5, NO WASTE</div>
            </div>
            {vehicle.has_roof && (
              <div className="inline-flex border mi-line-strong">
                {[true, false].map((v) => (
                  <button key={String(v)} type="button" onClick={() => setIncludeRoof(v)}
                          data-testid={`${testIdPrefix}-roof-${v ? 'on' : 'off'}`}
                          className={`px-3 py-1.5 text-[11px] mi-headline ${includeRoof === v ? 'bg-[#E63946] text-white' : 'text-white/60 hover:text-white'}`}>
                    {v ? 'Roof' : 'No roof'}
                  </button>
                ))}
              </div>
            )}
          </div>
          {showPanels && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <Panel k="Sides ×2" v={vehicle.side_sqft} dims={[vehicle.side_w, vehicle.side_h]} />
              <Panel k="Back" v={vehicle.back_sqft} dims={[vehicle.back_w, vehicle.back_h]} />
              <Panel k="Hood" v={vehicle.hood_sqft} dims={[vehicle.hood_w, vehicle.hood_l]} />
              <Panel k="Roof" v={vehicle.roof_sqft} dims={[vehicle.roof_w, vehicle.roof_l]} muted={!includeRoof} />
            </div>
          )}
          {vehicle.estimated && (
            <div className="mt-3 text-[11px] text-yellow-400/90 flex items-start gap-1.5" data-testid={`${testIdPrefix}-estimated`}>
              <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" /> Estimated — panels based on {vehicle.based_on}. Verify on the vehicle before cutting.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Panel({ k, v, dims, muted }) {
  const has = v != null && v > 0;
  return (
    <div className={`border mi-line p-2.5 ${muted ? 'opacity-40' : ''}`}>
      <div className="mi-eyebrow text-[9px] text-white/50 mb-1">{k}</div>
      <div className="font-mono-data text-white text-sm">{has ? `${v} sqft` : '—'}</div>
      {has && dims?.[0] && <div className="text-white/40 text-[10px] font-mono">{dims[0]}" × {dims[1]}"</div>}
    </div>
  );
}
